import React, { useEffect, useMemo, useState, useRef } from 'react';
import Select from 'react-select';
import rtoService from '../../services/rtoService';
import { Card } from '../../components/card/card.jsx';

const PROFILE_OPTIONS = [
  { value: 'safe-ad-recon', label: 'Safe AD Recon' },
  { value: 'safe', label: 'Safe' },
];

// Extended hints for profiles
const PROFILE_DETAILS = {
  'safe-ad-recon': {
    title: 'Safe AD Recon',
    description: 'Non-intrusive Active Directory reconnaissance: DNS lookups, LDAP schema read-only queries, NetBIOS/SMB discovery, and environment fingerprinting. No password spraying or disruptive actions.'
  },
  'safe': {
    title: 'Safe',
    description: 'General-purpose safe reconnaissance: host reachability, service banner collection, DNS/WHOIS, and passive discovery tailored to provided targets. Avoids aggressive scans.'
  }
};

const defaultTargets = {
  smb_host: '',
  ad_domain: '',
  ldap_uri: '',
  base_dn: '',
  dc_ip: '',
};

const defaultOptions = {
  username: '',
  bind_dn: '',
  anonymizer: '',
  // New: target check options
  probe_timeout_ms: '',
  udp_checks: false,
};

// --- Unified react-select styling to match Bootstrap look & feel ---
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 .25rem rgba(13,110,253,.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
    backgroundColor: 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)'
  }),
  valueContainer: (base) => ({ ...base, paddingTop: 2, paddingBottom: 2 }),
  indicatorsContainer: (base) => ({ ...base, paddingTop: 2, paddingBottom: 2 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({
    ...base,
    zIndex: 99999,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    border: '1px solid rgba(255,255,255,0.08)'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(13,110,253,.2)'
      : state.isSelected
      ? 'rgba(13,110,253,.35)'
      : 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    ':active': { backgroundColor: 'rgba(13,110,253,.35)' }
  }),
  singleValue: (base) => ({ ...base, color: 'var(--bs-body-color)' }),
  input: (base) => ({ ...base, color: 'var(--bs-body-color)' }),
  placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.6)' })
};

function AITools() {
  const [profile, setProfile] = useState(PROFILE_OPTIONS[0]);
  const [targets, setTargets] = useState(defaultTargets);
  const [options, setOptions] = useState(defaultOptions);
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(false); // used for summary refresh and legacy uses
  const [dryLoading, setDryLoading] = useState(false); // dry run progress
  const [applying, setApplying] = useState(false); // start & apply progress
  const [error, setError] = useState(null);
  const [orch, setOrch] = useState(null);
  const [appliedTasks, setAppliedTasks] = useState([]);
  // New: viewer state for policy.decision facts
  const [selectedPolicyFacts, setSelectedPolicyFacts] = useState(null);
  // New: orchestration summary state
  const [summary, setSummary] = useState(null);
  // New: anonymizer fact viewer state
  const [selectedAnonymizerFact, setSelectedAnonymizerFact] = useState(null);
  // New: tor facts viewer state
  const [selectedTorFacts, setSelectedTorFacts] = useState(null);
  // New: target check state
  const [targetCheck, setTargetCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  // New: plan mode and snapshot/meta for plan view
  const [planMode, setPlanMode] = useState('dry'); // 'dry' | 'real'
  const [planMeta, setPlanMeta] = useState(null); // { mode, generatedAt, note }
  const [planInputSnapshot, setPlanInputSnapshot] = useState(null); // { targets, options }
  
  // New: per-task insights for inline badges
  const [taskInsights, setTaskInsights] = useState({}); // { [taskId]: { anonymizer?: string, torSummary?: string } }
  const insightsLoadingRef = useRef(new Set());

  // New: timers and poller ref for progress UI
  const [dryElapsed, setDryElapsed] = useState(0);
  const [applyElapsed, setApplyElapsed] = useState(0);
  const applyPollRef = useRef(null);

  // Timer for Dry Run
  useEffect(() => {
    if (!dryLoading) return; // only run while generating
    setDryElapsed(0);
    const id = setInterval(() => setDryElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [dryLoading]);

  // Timer for Start & Apply (execution phase)
  useEffect(() => {
    if (!applying) return; // only run while executing
    setApplyElapsed(0);
    const id = setInterval(() => setApplyElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [applying]);

  // Cleanup poller on unmount
  useEffect(() => () => {
    if (applyPollRef.current) {
      clearInterval(applyPollRef.current);
      applyPollRef.current = null;
    }
  }, []);

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Allow check only when at least one target field is provided
  const hasAnyTarget = useMemo(() => Object.values(targets).some(v => String(v || '').trim() !== ''), [targets]);

  const profileLabel = useMemo(() => profile?.label || profile?.value || '', [profile]);

  const onChangeTargets = (e) => setTargets(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const onChangeOptions = (e) => setOptions(prev => ({ ...prev, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // Invalidate previous target check when targets change to ensure recency
  useEffect(() => { setTargetCheck(null); }, [targets]);

  // Successful target check flag
  const hasSuccessfulCheck = useMemo(() => !!(targetCheck && targetCheck.ok), [targetCheck]);

  // Snapshot equality with current inputs (to know if plan preview matches current)
  const snapshotEqualsCurrent = useMemo(() => {
    if (!planInputSnapshot) return false;
    try {
      return (
        JSON.stringify(planInputSnapshot.targets || {}) === JSON.stringify(targets || {}) &&
        JSON.stringify(planInputSnapshot.options || {}) === JSON.stringify(options || {})
      );
    } catch {
      return false;
    }
  }, [planInputSnapshot, targets, options]);

  const runDryRun = async () => {
    setDryLoading(true); setError(null);
    try {
      const res = await rtoService.dryRun(profile.value, targets, options);
      setDryRun(res);
      setPlanInputSnapshot({ targets: { ...targets }, options: { ...options } });
      setPlanMeta({ mode: planMode, generatedAt: new Date().toISOString(), note: planMode === 'real' ? 'Real executes the last Dry plan; preview generated via Dry Run.' : '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setDryLoading(false);
    }
  };

  // Compute execution progress percentage
  const applyProgress = useMemo(() => {
    const total = (appliedTasks && appliedTasks.length) || (dryRun?.plan?.length || 0);
    const doneSet = new Set(['completed', 'failed', 'succeeded', 'done', 'success']);
    const done = (appliedTasks || []).filter((t) => doneSet.has(String(t.status || '').toLowerCase())).length;
    if (!total) return applying ? 5 : 0; // minimal progress when starting
    const pct = Math.round((done / total) * 100);
    return Math.max(5, Math.min(100, pct));
  }, [appliedTasks, dryRun?.plan, applying]);

  const startAndApplyPlan = async () => {
    // Enforce gating rules
    if (planMode === 'real') {
      if (!dryRun?.plan?.length) { setError('Generate a plan first (Dry Run) — Real executes the last Dry plan.'); return; }
      if (!hasSuccessfulCheck) { setError('Please run a successful Target Check before executing in Real mode.'); return; }
    } else {
      if (!dryRun?.plan?.length) { setError('Generate a plan first (Dry mode).'); return; }
    }
    setApplying(true); setError(null); setAppliedTasks([]); setSummary(null);
    try {
      // Use combined start-and-apply endpoint
      const res = await rtoService.startAndApply(profile.value, targets, options);
      const orchId = res.orchestration_id;
      setOrch({ id: orchId, status: res.status, created_at: new Date().toISOString() });
      // Fetch tasks and initial summary
      const tasks = await rtoService.listTasks(orchId);
      setAppliedTasks(tasks);
      const s = await rtoService.getOrchestrationSummary(orchId);
      setSummary(s);

      // Start polling until completion
      if (applyPollRef.current) {
        clearInterval(applyPollRef.current);
        applyPollRef.current = null;
      }
      applyPollRef.current = setInterval(async () => {
        try {
          const tks = await rtoService.listTasks(orchId);
          setAppliedTasks(tks);
          const sum = await rtoService.getOrchestrationSummary(orchId);
          setSummary(sum);
          const total = tks.length;
          const doneSet = new Set(['completed', 'failed', 'succeeded', 'done', 'success']);
          const done = tks.filter((t) => doneSet.has(String(t.status || '').toLowerCase())).length;
          const finished = (total > 0 && done === total) || (sum && ['completed', 'done', 'failed', 'error'].includes(String(sum.status || '').toLowerCase()));
          if (finished) {
            clearInterval(applyPollRef.current);
            applyPollRef.current = null;
            setApplying(false);
          }
        } catch (pollErr) {
          // If polling fails repeatedly (e.g., server restart), stop the animation but keep last state
          clearInterval(applyPollRef.current);
          applyPollRef.current = null;
          setApplying(false);
        }
      }, 1500);
    } catch (e) {
      setError(e.message);
      setApplying(false);
    }
  };

  const checkTarget = async () => {
    if (!hasAnyTarget) { setError('Please fill at least one target field before checking.'); return; }
    setChecking(true); setError(null); setTargetCheck(null);
    try {
      const payload = {
        ...targets,
        // Probe timeout in ms; if empty or invalid, let backend default
        probe_timeout_ms: Number.isFinite(parseInt(options.probe_timeout_ms)) ? parseInt(options.probe_timeout_ms) : undefined,
        udp_checks: !!options.udp_checks,
      };
      const res = await rtoService.checkTarget(payload);
      setTargetCheck(res);
      if (!res.ok) {
        setError(res.note || 'Target check failed');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  };

  const planSummary = () => {
    if (!dryRun?.plan?.length) return 'No steps generated yet.';
    return `${dryRun.plan.length} step(s) planned.`;
  };

  const ANONYMIZER_OPTIONS = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'tor', label: 'Tor (torsocks)' },
  ]), []);

  const anonymizerSelectValue = useMemo(() => {
    const found = ANONYMIZER_OPTIONS.find(o => o.value === (options.anonymizer || ''));
    return found || ANONYMIZER_OPTIONS[0];
  }, [options.anonymizer, ANONYMIZER_OPTIONS]);

  const canPlan = useMemo(() => !!(dryRun?.plan?.length), [dryRun]);
  const canStartApply = useMemo(() => {
    if (planMode === 'real') {
      return hasAnyTarget && hasSuccessfulCheck && !!(dryRun?.plan?.length);
    }
    return !!(dryRun?.plan?.length);
  }, [planMode, hasAnyTarget, hasSuccessfulCheck, dryRun]);

  // Helpers to present plan overview
  const nonEmptyEntries = (obj = {}) => Object.entries(obj).filter(([_, v]) => String(v || '').trim() !== '');
  const toolsHeadline = useMemo(() => {
    if (!dryRun?.plan?.length) return '';
    const names = Array.from(new Set(dryRun.plan.map(s => s.tool))).filter(Boolean);
    const head = names.slice(0, 3).join(', ');
    const more = names.length > 3 ? ` +${names.length - 3} more` : '';
    return `${names.length} tool type(s): ${head}${more}`;
  }, [dryRun?.plan]);

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <Card className="mb-3">
            <div className="card-body">
              <h4 className="card-title mb-3">AI Tools - Policy-driven Recon</h4>
              <div className="form-text small mt-1 mb-2">
                    Descript: {PROFILE_DETAILS[profile?.value]?.description}
                  </div>
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">Profile <i className="fa fa-question-circle ms-1 text-muted" title="Select a predefined policy profile that determines safe reconnaissance steps."></i></label>
                  <Select
                    value={profile}
                    onChange={setProfile}
                    options={PROFILE_OPTIONS}
                    classNamePrefix="cp-select"
                    isSearchable={false}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#0d6efd',
                        primary25: '#1f2a3a',
                        neutral0: 'var(--bs-body-bg)',
                        neutral80: 'var(--bs-body-color)'
                      }
                    })}
                  />
                  
                </div>
                <div className="col-md-3">
                  <label className="form-label">Anonymizer <i className="fa fa-question-circle ms-1 text-muted" title="Route tool traffic through an anonymizer like Tor. Optional."></i></label>
                  <Select
                    value={anonymizerSelectValue}
                    onChange={(opt) => setOptions(prev => ({ ...prev, anonymizer: opt.value }))}
                    options={ANONYMIZER_OPTIONS}
                    classNamePrefix="cp-select"
                    isSearchable={false}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                  />
                </div>
                <div className="col-md-5 text-end">
                  <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                    <div className="btn-group btn-group-sm" role="group" aria-label="Plan mode">
                      <input type="radio" className="btn-check" name="planMode" id="modeDry" checked={planMode === 'dry'} onChange={() => setPlanMode('dry')} />
                      <label className="btn btn-outline-secondary" htmlFor="modeDry">Dry Run</label>
                      <input type="radio" className="btn-check" name="planMode" id="modeReal" checked={planMode === 'real'} onChange={async () => { setPlanMode('real'); if (!dryRun?.plan?.length || !snapshotEqualsCurrent) { await runDryRun(); } }} />
                      <label className="btn btn-outline-secondary" htmlFor="modeReal">Real</label>
                    </div>
                    <i className="fa fa-question-circle text-muted" title="Real executes the last Dry plan after you review it. Requires a successful Target Check before starting. Switching to Real will auto-generate a Dry plan if missing."></i>                    
                  </div>
                  <button className="btn btn-outline-secondary me-2" onClick={checkTarget} disabled={checking || !hasAnyTarget}>
                    {checking ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Checking…</>) : 'Check Target'}
                  </button>
                  <button className="btn btn-outline-primary me-2" onClick={runDryRun} disabled={dryLoading || applying}>
                    {dryLoading ? 'Generating…' : 'Run Plan'}
                  </button>
                  <button className="btn btn-primary" onClick={startAndApplyPlan} disabled={applying || !canStartApply}>
                    {applying ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Start & Apply…</>) : 'Start & Apply Plan'}
                  </button>

                  {/* Progress bars with timers */}
                  {dryLoading && (
                    <div className="mt-2">
                      <div className="progress">
                        <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}>
                          Generating plan… · {fmtTime(dryElapsed)}
                        </div>
                      </div>
                    </div>
                  )}
                  {applying && (
                    <div className="mt-2">
                      <div className="progress">
                        <div className="progress-bar progress-bar-striped progress-bar-animated bg-success" style={{ width: `${applyProgress}%` }}>
                          {applyProgress}% · Running… · {fmtTime(applyElapsed)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {targetCheck && (
                <div className="mt-3">
                  <div className={`alert ${targetCheck.ok ? 'alert-success' : 'alert-warning'} py-2 mb-2`}>
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{targetCheck.ok ? 'Target OK' : 'Needs Attention'}</strong> · {targetCheck.note}
                      </div>
                      <div className="small text-muted">IP: {targetCheck.ip || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="small">
                    <div className="mb-1"><strong>Guesses:</strong> Windows: {String(targetCheck.guess?.windows_like)} · DC: {String(targetCheck.guess?.domain_controller_like)}</div>
                    <div className="mb-1"><strong>Ports:</strong> {(targetCheck.ports||[]).map(p => `${p.port}:${p.open ? 'open' : 'closed'}`).join(', ')}</div>
                    {Array.isArray(targetCheck.suggestions) && targetCheck.suggestions.length > 0 && (
                      <ul className="mb-0">
                        {targetCheck.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Card className="mb-3">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center gap-2">Targets <i className="fa fa-question-circle text-muted" title="Provide any known environment info. The planner uses ONLY the fields you fill to generate safe steps (e.g., AD Domain enables LDAP/DC discovery; SMB Host enables SMB reachability and info-gathering)."></i></h5>
              <div className="text-muted small mb-2">These inputs seed the analysis plan. Nothing is executed until you review and start the plan.</div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">SMB Host <i className="fa fa-question-circle ms-1 text-muted" title="Windows SMB host IP or hostname (optional)"></i></label>
                  <input name="smb_host" value={targets.smb_host} onChange={onChangeTargets} className="form-control" placeholder="e.g. 10.0.0.10" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">AD Domain <i className="fa fa-question-circle ms-1 text-muted" title="Active Directory domain name, e.g. corp.local (optional)"></i></label>
                  <input name="ad_domain" value={targets.ad_domain} onChange={onChangeTargets} className="form-control" placeholder="e.g. corp.local" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">LDAP URI <i className="fa fa-question-circle ms-1 text-muted" title="LDAP server URI, e.g. ldap://dc.corp.local:389 (optional)"></i></label>
                  <input name="ldap_uri" value={targets.ldap_uri} onChange={onChangeTargets} className="form-control" placeholder="ldap://dc.corp.local:389" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Base DN <i className="fa fa-question-circle ms-1 text-muted" title="LDAP base distinguished name, e.g. DC=corp,DC=local (optional)"></i></label>
                  <input name="base_dn" value={targets.base_dn} onChange={onChangeTargets} className="form-control" placeholder="DC=corp,DC=local" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">DC IP <i className="fa fa-question-circle ms-1 text-muted" title="Domain Controller IP address (optional)"></i></label>
                  <input name="dc_ip" value={targets.dc_ip} onChange={onChangeTargets} className="form-control" placeholder="e.g. 10.0.0.5" />
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="card-body">
              <h5 className="card-title">Options</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Username <i className="fa fa-question-circle ms-1 text-muted" title="Optional username used by certain tools if needed."></i></label>
                  <input name="username" value={options.username} onChange={onChangeOptions} className="form-control" placeholder="optional" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Bind DN <i className="fa fa-question-circle ms-1 text-muted" title="Optional LDAP bind distinguished name if authentication is required."></i></label>
                  <input name="bind_dn" value={options.bind_dn} onChange={onChangeOptions} className="form-control" placeholder="optional" />
                </div>
                {/* Anonymizer is selected in the header controls */}
                <div className="col-md-6">
                  <label className="form-label">TCP probe timeout (ms) <i className="fa fa-question-circle ms-1 text-muted" title="How long to wait per port when checking the target (100–5000 ms)."></i></label>
                  <input type="number" min="50" max="5000" step="50" name="probe_timeout_ms" value={options.probe_timeout_ms} onChange={onChangeOptions} className="form-control" placeholder="400" />
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="udpChecks" name="udp_checks" checked={!!options.udp_checks} onChange={onChangeOptions} />
                    <label className="form-check-label" htmlFor="udpChecks">
                      Include UDP checks (DNS/Kerberos)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-lg-6">
          <Card>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title mb-0">Plan</h5>
                <small className="text-muted">{planSummary()}</small>
              </div>
              {planMode === 'real' && (
                <div className="form-text small mb-2">Real mode executes the last Dry plan. Ensure Target Check is successful before starting.</div>
              )}
              {error && (
                <div className="alert alert-danger py-2">{error}</div>
              )}
              {dryRun && (
                <div>
                  <div className="mb-2 d-flex flex-wrap align-items-center gap-2">
                    <span className="badge bg-secondary">Profile: {dryRun.profile}</span>
                    {dryRun.profile_signature && (
                      <span className="badge bg-info text-dark">Signature: {dryRun.profile_signature}</span>
                    )}
                    {options.anonymizer && (
                      <span className="badge bg-warning text-dark">Anonymizer: {options.anonymizer}</span>
                    )}
                    {planMeta?.mode && (
                      <span className={`badge ${planMeta.mode === 'real' ? 'bg-danger' : 'bg-light text-dark'}`}>Mode: {planMeta.mode === 'real' ? 'Real' : 'Dry Run'}</span>
                    )}
                    {planMeta?.note && (
                      <span className="badge bg-light text-dark">{planMeta.note}</span>
                    )}
                  </div>

                  {/* Confirmed Targets */}
                  <div className="mb-3">
                    <div className="fw-bold">Confirmed Targets</div>
                    <div className="small text-muted">Based on your inputs{targetCheck ? ' and target check' : ''}.</div>
                    <div className="d-flex flex-wrap gap-2 mt-1 small">
                      {nonEmptyEntries(planInputSnapshot?.targets || targets).map(([k,v]) => (
                        <span key={k} className="badge bg-secondary">{k.replace(/_/g,' ')}: {String(v)}</span>
                      ))}
                      {targetCheck?.ip && (
                        <span className="badge bg-dark">validated ip: {targetCheck.ip}</span>
                      )}
                      {targetCheck?.guess && (
                        <span className="badge bg-light text-dark">win-like: {String(targetCheck.guess.windows_like)} · dc-like: {String(targetCheck.guess.domain_controller_like)}</span>
                      )}
                      {(!nonEmptyEntries(planInputSnapshot?.targets || targets).length && !targetCheck) && (
                        <span className="text-muted">No explicit targets provided.</span>
                      )}
                    </div>
                  </div>

                  {/* Plan Description */}
                  <div className="mb-3">
                    <div className="fw-bold">What it will do</div>
                    <div className="small text-muted">{toolsHeadline || 'No steps generated.'}</div>
                    {Array.isArray(dryRun.plan) && dryRun.plan.length > 0 && (
                      <ul className="small mb-0 mt-1">
                        {dryRun.plan.slice(0, 3).map((s, i) => (
                          <li key={i}>
                            <strong>{s.tool}</strong>{s.explain?.length ? ` — ${s.explain[0]}` : ''}
                          </li>
                        ))}
                        {dryRun.plan.length > 3 && (
                          <li className="text-muted">…and {dryRun.plan.length - 3} more step(s)</li>
                        )}
                      </ul>
                    )}
                  </div>

                  {/* Steps */}
                  {!dryRun.plan.length ? (
                    <div className="text-muted">No steps for provided inputs.</div>
                  ) : (
                    <div className="list-group">
                      {dryRun.plan.map((s, idx) => (
                        <div key={idx} className="list-group-item">
                          <div className="d-flex justify-content-between">
                            <div><strong>{s.tool}</strong></div>
                            <div>
                              <span className="badge bg-light text-dark">{Object.keys(s.params || {}).length} param(s)</span>
                            </div>
                          </div>
                          {s.explain && s.explain.length > 0 && (
                            <ul className="mt-2 mb-0 small text-muted">
                              {s.explain.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          )}
                          {s.params && (
                            <pre className="mt-2 mb-0 small bg-dark text-white p-2 rounded"><code>{JSON.stringify(s.params, null, 2)}</code></pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {appliedTasks.length > 0 && (
                <div className="mt-3">
                  <h6>Applied Tasks</h6>
                  <div className="list-group">
                    {appliedTasks.map(t => (
                      <div key={t.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <div><strong>#{t.id}</strong> · {t.tool}</div>
                          <div className="small text-muted">Status: {t.status}</div>
                          {/* Inline badges: anonymizer + tor summary */}
                          <div className="small mt-1 d-flex flex-wrap gap-1">
                            {(() => {
                              const info = taskInsights[t.id] || {};
                              const anon = info.anonymizer || t?.external_meta?.options?.anonymizer || t?.external_meta?.anonymizer;
                              return anon ? (
                                <span className="badge bg-warning text-dark" title="Anonymizer used for this task">Anonymizer: {anon}</span>
                              ) : null;
                            })()}
                            {(() => {
                              const info = taskInsights[t.id] || {};
                              return info.torSummary ? (
                                <span className="badge bg-dark" title="Tor exit IP before → after">{info.torSummary}</span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={async (e) => {
                            try {
                              const facts = await rtoService.getTaskFacts(t.id);
                              const policyFacts = facts.filter(f => f.kind === 'policy.decision');
                              setSelectedPolicyFacts(policyFacts.length ? policyFacts[0].data : { message: 'No policy facts' });
                            } catch (e) {
                              setSelectedPolicyFacts({ error: e.message });
                            }
                          }}>View Policy Fact</button>
                          <button type="button" className="btn btn-sm btn-outline-warning" onClick={async () => {
                            try {
                              const facts = await rtoService.getTaskFacts(t.id);
                              const anonFacts = facts.filter(f => f.kind === 'anonymizer.applied');
                              setSelectedAnonymizerFact(anonFacts.length ? anonFacts[0].data : { message: 'No anonymizer used' });
                            } catch (e) {
                              setSelectedAnonymizerFact({ error: e.message });
                            }
                          }}>View Anonymizer</button>
                          <button type="button" className="btn btn-sm btn-outline-dark" onClick={async () => {
                            try {
                              const facts = await rtoService.getTaskFacts(t.id);
                              const torKinds = new Set([
                                'anonymizer.tor.status',
                                'anonymizer.tor.exit_ip.before',
                                'anonymizer.tor.control',
                                'anonymizer.tor.exit_ip.after',
                                'anonymizer.tor.session',
                              ]);
                              const torFacts = facts.filter(f => torKinds.has(f.kind));
                              if (!torFacts.length) {
                                setSelectedTorFacts({ message: 'No Tor facts available for this task.' });
                              } else {
                                // Collate by kind for easier reading
                                const grouped = torFacts.reduce((acc, f) => {
                                  acc[f.kind] = f.data;
                                  return acc;
                                }, {});
                                setSelectedTorFacts(grouped);
                              }
                            } catch (e) {
                              setSelectedTorFacts({ error: e.message });
                            }
                          }}>View Tor Facts</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedPolicyFacts && (
                    <div className="mt-3">
                      <h6>Policy Decision</h6>
                      <pre className="small bg-light p-2 rounded"><code>{JSON.stringify(selectedPolicyFacts, null, 2)}</code></pre>
                      <button className="btn btn-sm btn-secondary" onClick={() => setSelectedPolicyFacts(null)}>Close</button>
                    </div>
                  )}

                  {selectedAnonymizerFact && (
                    <div className="mt-3">
                      <h6>Anonymizer</h6>
                      <pre className="small bg-light p-2 rounded"><code>{JSON.stringify(selectedAnonymizerFact, null, 2)}</code></pre>
                      <button className="btn btn-sm btn-secondary" onClick={() => setSelectedAnonymizerFact(null)}>Close</button>
                    </div>
                  )}

                  {selectedTorFacts && (
                    <div className="mt-3">
                      <h6>Tor Facts</h6>
                      <pre className="small bg-light p-2 rounded"><code>{JSON.stringify(selectedTorFacts, null, 2)}</code></pre>
                      <button className="btn btn-sm btn-secondary" onClick={() => setSelectedTorFacts(null)}>Close</button>
                    </div>
                  )}

                  {orch && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Orchestration Summary</h6>
                        <button className="btn btn-sm btn-outline-primary" onClick={async () => {
                          try { setLoading(true); const s = await rtoService.getOrchestrationSummary(orch.id); setSummary(s); }
                          catch (e) { setError(e.message); }
                          finally { setLoading(false); }
                        }}>Refresh</button>
                      </div>
                      {summary ? (
                        <div className="mt-2">
                          <div className="small text-muted">Status: {summary.status}</div>
                          <div className="mt-2">
                            <strong>Tools</strong>
                            <ul className="small">
                              {(summary.tools || []).map((t, i) => (
                                <li key={i}>#{t.id} · {t.tool} · {t.status}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="mt-2">
                            <strong>Totals</strong>
                            <pre className="small bg-dark text-white p-2 rounded"><code>{JSON.stringify(summary.totals || {}, null, 2)}</code></pre>
                          </div>
                          {Array.isArray(summary.notes) && summary.notes.length > 0 && (
                            <div className="mt-2">
                              <strong>Notes</strong>
                              <ul className="small">
                                {summary.notes.map((n, i) => <li key={i}>{n}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-muted mt-2 small">No summary yet.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AITools;